/**
 * PluginBridge — Host-side bridge for communicating with plugin iframes.
 *
 * Handles postMessage communication, permission enforcement,
 * rate limiting, and proxying requests to the FIAS backend.
 */

import { invokeClientEntity, getClientEntityRegistration } from '@/lib/client-entity-registry';
import { getWebSocket } from '@/lib/websocket';
import { WEBSOCKET_ENDPOINT } from '@/lib/api';
import { authApi } from '@/lib/auth-api';

/**
 * Plugin permission scopes (matches @fias/db-types PluginPermission).
 * Redefined here to avoid client dependency on server-only package.
 *
 * SYNC NOTE: Keep in sync with:
 *   - packages/db-types/src/plugin.ts (PluginPermission)
 *   - packages/arche-sdk/src/types.ts (PluginPermission)
 */
type PluginPermission =
  | 'user:profile:read'
  | 'entities:invoke'
  | 'entities:image_generate'
  | 'entities:image_remove_background'
  | 'entities:audio_generate'
  | 'storage:sandbox'
  | 'theme:read'
  | 'data:store'
  | 'store:purchase'
  | 'vault:documents:read'
  | 'vault:documents:write'
  | 'assets:read';

// Bridge message types (must match the SDK's types)
interface BridgeMessage {
  type: string;
  messageId: string;
  payload: unknown;
}

/**
 * Bridge protocol version negotiation — host side.
 *
 *   HOST_BRIDGE_PROTOCOL_VERSION
 *     The protocol version this host SPEAKS. Bumped on backward-
 *     incompatible additions to the message shape.
 *
 *   HOST_REQUIRES_SDK_PROTOCOL_VERSION
 *     Minimum SDK protocol version this host accepts. If a plugin
 *     reports a lower version in its `ready` message, the host fires
 *     `onIncompatibleSdk` so the UI can render a "plugin needs update"
 *     affordance instead of letting the plugin call ops the host has
 *     removed.
 *
 * SYNC NOTE: must rise in lockstep with
 * packages/arche-sdk/src/protocol.ts when the protocol changes.
 */
const HOST_BRIDGE_PROTOCOL_VERSION = 1;
const HOST_REQUIRES_SDK_PROTOCOL_VERSION = 1;

interface PluginBridgeOptions {
  archId: string;
  permissions: PluginPermission[];
  iframe: HTMLIFrameElement;
  onResize?: (height: number) => void;
  onToast?: (message: string, variant: string) => void;
  onNavigate?: (path: string) => void;
  getTheme: () => unknown;
  getUser: () => Promise<{ userId: string; displayName: string; avatar: string | null }>;
  bridgeApiUrl: string;
  getAuthToken: () => Promise<string>;
  /** When true, uses local mocks for storage and custom handler for entity invoke. */
  previewMode?: boolean;
  /** Custom entity_invoke handler for preview (avoids backend proxy for entity lookup). */
  onEntityInvoke?: (payload: {
    entityId: string;
    input: string;
    systemPrompt?: string;
  }) => Promise<{ output: string }>;
  /** Custom image_generate handler for preview (avoids backend proxy for image config lookup). */
  onImageGenerate?: (payload: {
    entityId: string;
    prompt: string;
    size?: string;
    quality?: string;
    style?: string;
  }) => Promise<{
    imageUrl: string;
    imageRef: string;
    revisedPrompt?: string;
    provider: string;
    model: string;
  }>;
  /** External storage map for preview mode — survives bridge rebuilds when passed from parent. */
  previewStorage?: Map<string, string>;
  /** Custom storage handler for preview mode — when provided, proxies to backend instead of in-memory. */
  onStorage?: (action: string, params: Record<string, unknown>) => Promise<unknown>;
  /** Callback for bridge-level log events (e.g., burst detection warnings). */
  onLog?: (level: 'warn' | 'error', message: string) => void;
  /**
   * Fired when the SDK acknowledges init with a `sdkProtocolVersion`
   * lower than `HOST_REQUIRES_SDK_PROTOCOL_VERSION`. The host UI should
   * unmount the iframe and render a "plugin needs update" affordance.
   * Receives the actual SDK version the plugin reported.
   */
  onIncompatibleSdk?: (sdkProtocolVersion: number, minRequired: number) => void;
  /**
   * Callback for IAP purchase requests. When set, store_purchase messages
   * invoke this instead of proxying directly to backend. The callback should
   * show a confirmation UI and resolve with the backend response on confirm,
   * or { success: false, error: 'USER_CANCELED' } on cancel.
   */
  onPurchaseRequest?: (product: {
    productIdentifier: string;
    name: string;
    description: string | null;
    priceCredits: number;
    type: string;
  }) => Promise<unknown>;
  /** Mock store products for preview mode. */
  previewStoreProducts?: Array<{
    productIdentifier: string;
    type: string;
    name: string;
    description: string | null;
    priceCredits: number;
    metadata: Record<string, unknown>;
    subscriptionPeriod?: string;
  }>;
}

/**
 * Rate limiting state per message type.
 */
interface RateLimitBucket {
  count: number;
  windowStart: number;
}

const RATE_LIMITS: Record<string, { maxPerMinute: number }> = {
  entity_invoke: { maxPerMinute: 60 },
  client_entity_invoke: { maxPerMinute: 60 },
  image_generate: { maxPerMinute: 10 },
  audio_generate: { maxPerMinute: 10 },
  storage_write: { maxPerMinute: 120 },
  storage_read: { maxPerMinute: 300 },
  storage_list: { maxPerMinute: 60 },
  storage_delete: { maxPerMinute: 60 },
  data_put: { maxPerMinute: 120 },
  data_get: { maxPerMinute: 300 },
  data_query: { maxPerMinute: 60 },
  data_delete: { maxPerMinute: 60 },
  data_create_collection: { maxPerMinute: 10 },
  data_delete_collection: { maxPerMinute: 10 },
  data_list_collections: { maxPerMinute: 60 },
  store_get_products: { maxPerMinute: 60 },
  store_purchase: { maxPerMinute: 10 },
  store_get_entitlements: { maxPerMinute: 60 },
  store_get_purchase_history: { maxPerMinute: 30 },
  store_restore: { maxPerMinute: 10 },
  store_cancel_subscription: { maxPerMinute: 10 },
  vault_documents_list: { maxPerMinute: 120 },
  vault_documents_read: { maxPerMinute: 300 },
  // Batch reads count as ONE bridge call. Per-doc rate-limiting on the
  // batch payload (max 50 ids per call) is enforced server-side.
  vault_documents_read_many: { maxPerMinute: 60 },
  vault_documents_download_url: { maxPerMinute: 300 },
  vault_documents_get_urls: { maxPerMinute: 60 },
  vault_documents_write: { maxPerMinute: 60 },
  vault_documents_update: { maxPerMinute: 60 },
  vault_documents_delete: { maxPerMinute: 30 },
  arche_assets_list: { maxPerMinute: 60 },
  arche_assets_get_url: { maxPerMinute: 300 },
};

/** Burst detection: catches runaway loops (e.g., useEffect calling dataStore.put every render). */
interface BurstBucket {
  count: number;
  windowStart: number;
  /** When set, all calls of this type are blocked until this timestamp. */
  blockedUntil?: number;
}

const BURST_THRESHOLD = 50;
const BURST_WINDOW_MS = 5_000;
const BURST_COOLDOWN_MS = 10_000;

/**
 * Extracts a short diagnostic identifier from a bridge call's payload so that
 * rate-limit and runaway-loop errors can point to the specific path/resource
 * being hammered (e.g. `__state/gameState` when a game loop is spamming
 * `usePersistentState` writes). Returns an empty string for call types where
 * no sensible target exists.
 */
function describeBridgeTarget(type: string, payload: unknown): string {
  if (!payload || typeof payload !== 'object') return '';
  const p = payload as Record<string, unknown>;

  switch (type) {
    case 'storage_read':
    case 'storage_write':
    case 'storage_delete':
      return typeof p.path === 'string' ? `path: ${p.path}` : '';
    case 'storage_list':
      return typeof p.prefix === 'string' ? `prefix: ${p.prefix}` : '';
    case 'data_put':
    case 'data_get':
    case 'data_delete': {
      const collection = typeof p.collection === 'string' ? p.collection : '';
      const docId = typeof p.docId === 'string' ? p.docId : '';
      if (collection && docId) return `${collection}/${docId}`;
      return collection ? `collection: ${collection}` : '';
    }
    case 'data_query':
    case 'data_create_collection':
    case 'data_delete_collection':
      return typeof p.collection === 'string' ? `collection: ${p.collection}` : '';
    case 'entity_invoke':
    case 'client_entity_invoke':
      return typeof p.entityId === 'string' ? `entity: ${p.entityId}` : '';
    case 'navigate':
      return typeof p.path === 'string' ? `path: ${p.path}` : '';
    case 'vault_documents_read':
    case 'vault_documents_download_url':
    case 'vault_documents_update':
    case 'vault_documents_delete':
      return typeof p.documentId === 'string' ? `documentId: ${p.documentId}` : '';
    case 'vault_documents_write':
      return typeof p.name === 'string' ? `name: ${p.name}` : '';
    case 'vault_documents_list':
      return typeof p.search === 'string' ? `search: ${p.search}` : '';
    default:
      return '';
  }
}

// Thrown by checkBurst when a call is blocked for runaway-loop reasons. The
// burst path already fires onLog on the first breach and deliberately stays
// silent during cooldown — the outer request-failure catch uses this tag to
// skip its own onLog so we don't double-log the breach and re-log every
// cooldown-blocked call.
class BridgeBurstError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'BridgeBurstError';
  }
}

// Preview-mode storage caps. Preview mode is explicitly not production —
// these caps exist so a preview-mode plugin can't pin the renderer tab by
// writing unbounded state into the in-memory Maps. Production writes go
// through the backend and are bounded by the datastore quota middleware.
const PREVIEW_STORAGE_MAX_ENTRIES = 10_000;
const PREVIEW_STORAGE_MAX_TOTAL_BYTES = 5 * 1024 * 1024; // 5 MB
const PREVIEW_DATASTORE_MAX_DOCS_PER_COLLECTION = 5_000;
const PREVIEW_DATASTORE_MAX_DOC_BYTES = 256 * 1024; // 256 KB per doc

/**
 * Permission requirements per bridge call type.
 */
const PERMISSION_MAP: Record<string, PluginPermission> = {
  get_user: 'user:profile:read',
  get_theme: 'theme:read',
  entity_invoke: 'entities:invoke',
  image_generate: 'entities:image_generate',
  entities_list_image: 'entities:image_generate',
  audio_generate: 'entities:audio_generate',
  // `client_entity_invoke` deliberately omitted: the required permission
  // is per-entity (resolved from `pluginInvocable.pluginPermission` at
  // request time inside the case block), so the centralized map can't
  // answer statically.
  storage_read: 'storage:sandbox',
  storage_write: 'storage:sandbox',
  storage_list: 'storage:sandbox',
  storage_delete: 'storage:sandbox',
  data_create_collection: 'data:store',
  data_list_collections: 'data:store',
  data_delete_collection: 'data:store',
  data_put: 'data:store',
  data_get: 'data:store',
  data_query: 'data:store',
  data_delete: 'data:store',
  store_get_products: 'store:purchase',
  store_purchase: 'store:purchase',
  store_get_entitlements: 'store:purchase',
  store_get_purchase_history: 'store:purchase',
  store_restore: 'store:purchase',
  store_cancel_subscription: 'store:purchase',
  vault_documents_list: 'vault:documents:read',
  vault_documents_read: 'vault:documents:read',
  vault_documents_read_many: 'vault:documents:read',
  vault_documents_download_url: 'vault:documents:read',
  vault_documents_search: 'vault:documents:read',
  vault_documents_get_urls: 'vault:documents:read',
  vault_documents_write: 'vault:documents:write',
  vault_documents_update: 'vault:documents:write',
  vault_documents_delete: 'vault:documents:write',
  vault_documents_attach: 'vault:documents:write',
  vault_documents_detach: 'vault:documents:write',
  vault_documents_upload_init: 'vault:documents:write',
  vault_documents_upload_finalize: 'vault:documents:write',
  arche_assets_list: 'assets:read',
  arche_assets_get_url: 'assets:read',
};

export class PluginBridgeHost {
  private options: PluginBridgeOptions;
  private rateLimits = new Map<string, RateLimitBucket>();
  private burstBuckets = new Map<string, BurstBucket>();
  /** In-memory storage for preview mode (no backend calls needed). */
  private previewStorage: Map<string, string>;
  /** In-memory data store for preview mode — maps collection → (key → document). */
  private previewDataStore = new Map<string, Map<string, Record<string, unknown>>>();
  /** Collection metadata for preview data store. */
  private previewCollections = new Map<
    string,
    { name: string; userScope: string; createdAt: string }
  >();

  private initRetryTimers: ReturnType<typeof setTimeout>[] = [];

  /**
   * In-flight WS-async bridge requests (today: `image_generate`).
   *
   * Each entry holds the Promise resolver pair that completes the
   * outer `handleRequest` Promise, plus a cleanup callback that
   * unsubscribes the WS listeners and clears the safety timeout. Keyed
   * by the server-issued `requestId` so the WS callbacks (which fire
   * for every in-flight subscription the user owns) can filter to the
   * matching entry and ignore broadcasts for other plugins/contexts.
   *
   * Cleanup runs on EVERY terminal path — WS success, WS error,
   * safety-timeout fallback, host `destroy()` — so listeners cannot
   * leak across plugin sessions. See `plan §"Cleanup contract"`.
   */
  private pendingImageRequests = new Map<
    string,
    {
      resolve: (value: unknown) => void;
      reject: (err: Error) => void;
      cleanup: () => void;
      timer: ReturnType<typeof setTimeout>;
    }
  >();

  constructor(options: PluginBridgeOptions) {
    this.options = options;
    this.previewStorage = options.previewStorage ?? new Map<string, string>();
    this.handleMessage = this.handleMessage.bind(this);
    window.addEventListener('message', this.handleMessage);
  }

  /**
   * Send the init message to the plugin iframe to establish the bridge.
   *
   * When loading via srcDoc, the iframe's onLoad fires before ES module
   * scripts (loaded from esm.sh) have executed. The SDK's message listener
   * may not exist yet, so we retry init a few times with increasing delays.
   * The SDK ignores duplicate init messages after the first.
   */
  init(): void {
    this.sendInit();
    this.initRetryTimers = [
      setTimeout(() => this.sendInit(), 200),
      setTimeout(() => this.sendInit(), 1000),
      setTimeout(() => this.sendInit(), 3000),
    ];
  }

  private sendInit(): void {
    this.sendToPlugin({
      type: 'init',
      messageId: 'init_0',
      payload: {
        archId: this.options.archId,
        permissions: this.options.permissions,
        theme: this.options.getTheme(),
        currentPath: window.location.pathname,
        // Protocol-version handshake (see HOST_BRIDGE_PROTOCOL_VERSION
        // block above). The SDK rejects hosts older than its required
        // minimum; the host rejects plugins older than ours via the
        // `ready` message handler below.
        hostProtocolVersion: HOST_BRIDGE_PROTOCOL_VERSION,
        minSdkProtocolVersion: HOST_REQUIRES_SDK_PROTOCOL_VERSION,
      },
    });
  }

  /**
   * Notify the plugin of a theme change.
   */
  sendThemeUpdate(): void {
    this.sendToPlugin({
      type: 'theme_update',
      messageId: `theme_${Date.now()}`,
      payload: this.options.getTheme(),
    });
  }

  /**
   * Navigate the plugin to a specific step/node.
   */
  navigateToStep(nodeId: string): void {
    this.sendToPlugin({
      type: 'step_navigate',
      messageId: `step_${Date.now()}`,
      payload: { nodeId },
    });
  }

  /**
   * Clean up event listeners and release all in-memory state.
   *
   * The per-call Maps (rateLimits, burstBuckets) are bounded by the number
   * of unique message types (~20) so they don't grow unbounded on their
   * own, but preview Maps track plugin-controlled keys. Clearing them here
   * prevents re-mount loops in dev from accumulating state across hosts.
   */
  destroy(): void {
    for (const timer of this.initRetryTimers) clearTimeout(timer);
    this.initRetryTimers = [];
    // Tear down any in-flight WS-async bridge requests so the iframe's
    // awaiting Promises reject deterministically. The cleanup function
    // unsubs the WS listeners + clears the safety timer; the reject
    // surfaces to the outer `handleRequest` await which posts an error
    // response into the iframe.
    for (const [, entry] of this.pendingImageRequests) {
      entry.cleanup();
      entry.reject(new Error('plugin_unmounted'));
    }
    this.pendingImageRequests.clear();
    window.removeEventListener('message', this.handleMessage);
    this.rateLimits.clear();
    this.burstBuckets.clear();
    this.previewStorage.clear();
    this.previewDataStore.clear();
    this.previewCollections.clear();
  }

  /**
   * Returns the total byte size of the preview storage Map. Cheap since
   * preview storage values are always strings.
   */
  private getPreviewStorageTotalBytes(): number {
    let total = 0;
    for (const [key, value] of this.previewStorage) {
      total += key.length + value.length;
    }
    return total;
  }

  private async handleMessage(event: MessageEvent): Promise<void> {
    // Only handle messages from our plugin iframe
    if (event.source !== this.options.iframe.contentWindow) return;

    const data = event.data;
    if (!data || typeof data !== 'object' || !data.type) return;

    // Plugin SDK requesting init (handles srcDoc module loading race condition)
    if (data.type === 'request_init') {
      this.sendInit();
      return;
    }

    // All other messages require messageId
    if (!(data as BridgeMessage).messageId) return;

    // Fire-and-forget messages (no response needed)
    if (data.type === 'ready') {
      // Plugin acknowledged init — stop retrying
      for (const timer of this.initRetryTimers) clearTimeout(timer);
      this.initRetryTimers = [];

      // Protocol-version compatibility check. The SDK ≥ v1 sends its
      // `sdkProtocolVersion` in the ready payload. An older SDK omits
      // the field — that's treated as v1 (the first published contract),
      // which is forward-compatible if our minimum is also v1. Once we
      // bump HOST_REQUIRES_SDK_PROTOCOL_VERSION past 1, a missing field
      // becomes "too old" automatically.
      const payload = (data as BridgeMessage).payload as
        | { sdkProtocolVersion?: number }
        | undefined;
      const sdkVersion = payload?.sdkProtocolVersion ?? 1;
      if (sdkVersion < HOST_REQUIRES_SDK_PROTOCOL_VERSION) {
        this.options.onLog?.(
          'warn',
          `Plugin bridge SDK v${sdkVersion} is older than the minimum this host accepts (v${HOST_REQUIRES_SDK_PROTOCOL_VERSION}).`,
        );
        this.options.onIncompatibleSdk?.(sdkVersion, HOST_REQUIRES_SDK_PROTOCOL_VERSION);
      }
      return;
    }

    if (data.type === 'resize') {
      const { height } = data.payload as { height: number };
      if (typeof height === 'number' && height > 0) {
        this.options.onResize?.(height);
      }
      return;
    }

    if (data.type === 'toast') {
      const { message, variant } = data.payload as { message: string; variant: string };
      if (typeof message === 'string') {
        this.options.onToast?.(message, variant ?? 'info');
      }
      return;
    }

    if (data.type === 'navigate') {
      const { path } = data.payload as { path: string };
      if (typeof path !== 'string' || path.length === 0) {
        const message = `Bridge navigate rejected: path must be a non-empty string (got: ${typeof path === 'string' ? '<empty>' : '<missing>'})`;
        this.options.onLog?.('error', message);
        this.sendError(data.messageId, message);
        return;
      }
      // Plugins navigate in their own URL space (e.g. `/edit/<id>` or
      // `/shared/<id>`); the platform's URL space is `/a/<archeId>/...`.
      // Auto-prepend the plugin's own arche prefix when the plugin sends
      // a root-relative path so plugin code doesn't have to know its own
      // archeId or the platform path convention. Mirrors how `currentPath`
      // is delivered inbound — both sides agree on the platform path.
      //
      // Security: we always scope to *this plugin's own* archeId. A path
      // already in `/a/<own-archeId>/...` form is passed through; one
      // pointing at a different arche is rejected so a plugin can't
      // navigate the parent window into someone else's arche space.
      const ownPrefix = `/a/${this.options.archId}`;
      let resolved: string | null = null;
      if (path === ownPrefix || path.startsWith(`${ownPrefix}/`)) {
        resolved = path;
      } else if (path.startsWith('/a/')) {
        const message = `Bridge navigate rejected: cross-arche navigation not allowed (got: ${path})`;
        this.options.onLog?.('error', message);
        this.sendError(data.messageId, message);
        return;
      } else if (path.startsWith('/')) {
        resolved = path === '/' ? ownPrefix : `${ownPrefix}${path}`;
      }
      if (!resolved) {
        const message = `Bridge navigate rejected: path must be root-relative (got: ${path})`;
        this.options.onLog?.('error', message);
        this.sendError(data.messageId, message);
        return;
      }
      this.options.onNavigate?.(resolved);
      // Acknowledge so the plugin's `await bridge.request('navigate', ...)`
      // resolves instead of timing out 30s later.
      this.sendResponse(data.messageId, null);
      return;
    }

    // Request/response messages — check permissions and rate limits
    try {
      const result = await this.handleRequest(data);
      this.sendResponse(data.messageId, result);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      // Surface the error to the preview log stream so get_preview_logs picks
      // it up. Without this the plugin gets a rejected Promise but the AI
      // has no visibility unless the plugin code explicitly console.errors.
      // BridgeBurstError is the exception: the burst path manages its own
      // onLog (fires once on breach, silent during cooldown) — re-logging
      // here would double-log the breach and drown the logs during cooldown.
      if (!(err instanceof BridgeBurstError)) {
        this.options.onLog?.('error', `Bridge request failed (${data.type}): ${message}`);
      }
      this.sendError(data.messageId, message);
    }
  }

  private async handleRequest(msg: BridgeMessage): Promise<unknown> {
    // In preview mode, skip permission checks (all permissions granted)
    if (!this.options.previewMode) {
      const requiredPermission = PERMISSION_MAP[msg.type];
      if (requiredPermission && !this.options.permissions.includes(requiredPermission)) {
        throw new Error(`Permission denied: ${requiredPermission} not granted`);
      }
    }

    // Rate limiting applies in all modes (protects against runaway loops in preview)
    this.checkRateLimit(msg.type, msg.payload);

    switch (msg.type) {
      case 'get_user':
        return this.options.getUser();

      case 'get_theme':
        return this.options.getTheme();

      case 'entity_invoke': {
        const payload = msg.payload as {
          entityId: string;
          input: string;
          stream?: boolean;
          systemPrompt?: string;
        };
        if (this.options.previewMode && this.options.onEntityInvoke) {
          if (payload.stream) {
            return this.handlePreviewStreamingInvoke(msg.messageId, payload);
          }
          return this.options.onEntityInvoke(payload);
        }
        // Published plugins: handle streaming via SSE through the bridge endpoint
        if (payload.stream) {
          return this.handlePublishedStreamingInvoke(msg.messageId, payload);
        }
        return this.proxyToBackend(msg);
      }

      case 'image_generate': {
        const imgPayload = msg.payload as {
          entityId: string;
          prompt: string;
          size?: string;
          quality?: string;
          style?: string;
        };
        if (this.options.previewMode && this.options.onImageGenerate) {
          return this.options.onImageGenerate(imgPayload);
        }
        // Async-via-WebSocket path: POST returns `{ requestId, status:
        // 'pending' }` immediately; the final `ImageInvokeResult`
        // arrives over WS as an `image_complete` broadcast. Pre-refactor
        // this case awaited the full 30–90 s provider call on a single
        // HTTP request and hit the internal ALB's 60 s idle timeout. See
        // image-generate-bridge-handler.ts file header.
        return this.handleAsyncImageGenerate(msg);
      }

      case 'entities_list_image': {
        // Discovery op backing the SDK's `useImageEntities` hook. Pure
        // registry read on the server — no preview-mode hook needed
        // (preview can call the live registry and stays in sync with
        // production).
        return this.proxyToBackend(msg);
      }

      case 'client_entity_invoke': {
        // Generic dispatcher for `executionMode: 'client-side'` entities.
        // Resolves the entity registration by ID (from the client-side
        // registry — populated by executor module imports at startup),
        // gates on the entity's declared `pluginPermission`, and hands off
        // to the registry. No per-entity case needed: adding a new
        // client-side entity is an entity-config + executor change, never
        // a bridge change.
        const payload =
          msg.payload && typeof msg.payload === 'object'
            ? (msg.payload as { entityId?: unknown; input?: unknown })
            : null;
        if (!payload || typeof payload.entityId !== 'string') {
          throw new Error('client_entity_invoke: payload.entityId (string) is required');
        }
        const registration = getClientEntityRegistration(payload.entityId);
        if (!registration) {
          throw new Error(
            `client_entity_invoke: no client-side executor registered for ${payload.entityId}`,
          );
        }
        const requiredPermission = registration.pluginPermission as PluginPermission;
        if (!this.options.previewMode && !this.options.permissions.includes(requiredPermission)) {
          throw new Error(`Permission denied: ${requiredPermission} not granted`);
        }
        const user = await this.options.getUser();
        return invokeClientEntity(payload.entityId, payload.input, {
          userId: user.userId,
          archeId: this.options.archId,
        });
      }

      case 'audio_generate': {
        // Audio generation runs through the server-side `audio_generate`
        // bridge op. Preview-mode hooks (parallel to onImageGenerate) can
        // be added when a preview-side mock is needed; for v1 we proxy
        // straight to the backend, which dispatches to the music model.
        return this.proxyToBackend(msg);
      }

      case 'storage_read': {
        if (this.options.previewMode) {
          if (this.options.onStorage) {
            const { path } = msg.payload as { path?: string };
            return this.options.onStorage('read', { path });
          }
          const { path } = msg.payload as { path?: string };
          const content = this.previewStorage.get(path ?? '') ?? null;
          return { content, exists: content !== null };
        }
        return this.proxyToBackend(msg);
      }

      case 'storage_write': {
        if (this.options.previewMode) {
          if (this.options.onStorage) {
            const { path, content } = msg.payload as { path?: string; content?: string };
            return this.options.onStorage('write', { path, content });
          }
          const { path, content } = msg.payload as { path?: string; content?: string };
          if (path && content !== undefined) {
            // Enforce preview storage caps so a preview plugin can't pin
            // the renderer tab with unbounded writes.
            const isNewEntry = !this.previewStorage.has(path);
            if (isNewEntry && this.previewStorage.size >= PREVIEW_STORAGE_MAX_ENTRIES) {
              throw new Error(
                `Preview storage entry limit exceeded (${PREVIEW_STORAGE_MAX_ENTRIES} entries).`,
              );
            }
            const priorBytes = isNewEntry
              ? 0
              : path.length + (this.previewStorage.get(path)?.length ?? 0);
            const newBytes = path.length + content.length;
            if (
              this.getPreviewStorageTotalBytes() - priorBytes + newBytes >
              PREVIEW_STORAGE_MAX_TOTAL_BYTES
            ) {
              throw new Error(
                `Preview storage size limit exceeded (${PREVIEW_STORAGE_MAX_TOTAL_BYTES} bytes).`,
              );
            }
            this.previewStorage.set(path, content);
          }
          return { success: true };
        }
        return this.proxyToBackend(msg);
      }

      case 'storage_list': {
        if (this.options.previewMode) {
          if (this.options.onStorage) {
            const { prefix } = msg.payload as { prefix?: string };
            return this.options.onStorage('list', { prefix });
          }
          const { prefix } = msg.payload as { prefix?: string };
          const files = [...this.previewStorage.keys()].filter(
            (k) => !prefix || k.startsWith(prefix),
          );
          return { files };
        }
        return this.proxyToBackend(msg);
      }

      case 'storage_delete': {
        if (this.options.previewMode) {
          if (this.options.onStorage) {
            const { path } = msg.payload as { path?: string };
            return this.options.onStorage('delete', { path });
          }
          const { path } = msg.payload as { path?: string };
          if (path) this.previewStorage.delete(path);
          return { success: true };
        }
        return this.proxyToBackend(msg);
      }

      // ── Data Store (in-memory for preview mode) ─────────────────────

      case 'data_create_collection': {
        if (this.options.previewMode) {
          const { name, userScope } = msg.payload as { name: string; userScope?: string };
          if (!this.previewDataStore.has(name)) {
            this.previewDataStore.set(name, new Map());
          }
          const meta = {
            name,
            userScope: userScope ?? 'user',
            createdAt: new Date().toISOString(),
          };
          this.previewCollections.set(name, meta);
          return { collection: meta };
        }
        return this.proxyToBackend(msg);
      }

      case 'data_list_collections': {
        if (this.options.previewMode) {
          return { collections: [...this.previewCollections.values()] };
        }
        return this.proxyToBackend(msg);
      }

      case 'data_delete_collection': {
        if (this.options.previewMode) {
          const { name } = msg.payload as { name: string };
          this.previewDataStore.delete(name);
          this.previewCollections.delete(name);
          return { success: true };
        }
        return this.proxyToBackend(msg);
      }

      case 'data_put': {
        if (this.options.previewMode) {
          const { collection, key, data } = msg.payload as {
            collection: string;
            key: string;
            data: Record<string, unknown>;
          };
          // Enforce preview data store caps — per-doc size + per-collection
          // entry count. Blocks unbounded writes in preview mode.
          const serialized = JSON.stringify(data);
          if (serialized.length > PREVIEW_DATASTORE_MAX_DOC_BYTES) {
            throw new Error(
              `Preview data store document size exceeds limit (${PREVIEW_DATASTORE_MAX_DOC_BYTES} bytes).`,
            );
          }
          let col = this.previewDataStore.get(collection);
          if (!col) {
            // Auto-create collection on first put
            col = new Map();
            this.previewDataStore.set(collection, col);
            this.previewCollections.set(collection, {
              name: collection,
              userScope: 'user',
              createdAt: new Date().toISOString(),
            });
          }
          if (!col.has(key) && col.size >= PREVIEW_DATASTORE_MAX_DOCS_PER_COLLECTION) {
            throw new Error(
              `Preview data store collection "${collection}" exceeds document limit (${PREVIEW_DATASTORE_MAX_DOCS_PER_COLLECTION}).`,
            );
          }
          col.set(key, { ...data, _key: key, _updatedAt: new Date().toISOString() });
          return { success: true };
        }
        return this.proxyToBackend(msg);
      }

      case 'data_get': {
        if (this.options.previewMode) {
          const { collection, key } = msg.payload as { collection: string; key: string };
          const doc = this.previewDataStore.get(collection)?.get(key) ?? null;
          return { data: doc };
        }
        return this.proxyToBackend(msg);
      }

      case 'data_query': {
        if (this.options.previewMode) {
          const { collection, limit, orderBy } = msg.payload as {
            collection: string;
            filters?: Array<{ field: string; op: string; value: unknown }>;
            orderBy?: { field: string; direction: 'asc' | 'desc' };
            limit?: number;
          };
          const col = this.previewDataStore.get(collection);
          if (!col) return { documents: [], nextCursor: null };
          let docs = [...col.entries()].map(([k, v]) => ({
            key: k,
            data: v,
            createdAt: (v._updatedAt as string) ?? new Date().toISOString(),
            updatedAt: (v._updatedAt as string) ?? new Date().toISOString(),
          }));
          if (orderBy) {
            docs.sort((a, b) => {
              const aVal = a.data[orderBy.field];
              const bVal = b.data[orderBy.field];
              if (aVal === bVal) return 0;
              const cmp = aVal != null && bVal != null && aVal < bVal ? -1 : 1;
              return orderBy.direction === 'desc' ? -cmp : cmp;
            });
          }
          if (limit) docs = docs.slice(0, limit);
          return { documents: docs, nextCursor: null };
        }
        return this.proxyToBackend(msg);
      }

      case 'data_delete': {
        if (this.options.previewMode) {
          const { collection, key } = msg.payload as { collection: string; key: string };
          this.previewDataStore.get(collection)?.delete(key);
          return { success: true };
        }
        return this.proxyToBackend(msg);
      }

      // ── Store / IAP ────────────────────────────────────────────────

      case 'store_get_products': {
        if (this.options.previewMode) {
          return this.options.previewStoreProducts ?? [];
        }
        return this.proxyToBackend(msg);
      }

      case 'store_purchase': {
        if (this.options.previewMode) {
          // In preview, simulate a successful purchase
          const { productIdentifier } = msg.payload as { productIdentifier: string };
          return {
            success: true,
            purchaseId: `preview_apc_${Date.now()}`,
            product: { productIdentifier, productType: 'consumable', name: productIdentifier },
          };
        }
        // Published mode: delegate to purchase confirmation callback if available
        if (this.options.onPurchaseRequest) {
          const { productIdentifier } = msg.payload as { productIdentifier: string };
          // Look up product info from backend first
          const products = (await this.proxyToBackend({
            ...msg,
            type: 'store_get_products' as BridgeMessage['type'],
            payload: {},
          })) as Array<{
            productIdentifier: string;
            name: string;
            description: string | null;
            priceCredits: number;
            type: string;
          }>;
          const product = Array.isArray(products)
            ? products.find((p) => p.productIdentifier === productIdentifier)
            : undefined;
          if (!product) {
            return { success: false, error: 'Product not found' };
          }
          // Delegate to confirmation UI
          return this.options.onPurchaseRequest(product);
        }
        // Fallback: proxy directly (no confirmation overlay)
        return this.proxyToBackend(msg);
      }

      case 'store_get_entitlements':
      case 'store_get_purchase_history':
      case 'store_restore':
      case 'store_cancel_subscription': {
        if (this.options.previewMode) {
          // In preview, return empty results
          if (msg.type === 'store_get_entitlements' || msg.type === 'store_restore') return [];
          if (msg.type === 'store_get_purchase_history') return { purchases: [] };
          return { success: true };
        }
        return this.proxyToBackend(msg);
      }

      // ── Vault Documents (Phase 12 substrate, scoped to source_arche_id) ──
      // The server-side bridge handler enforces source_arche_id == this arche
      // (or attachment via vault_document_references) and proxies through
      // vault-documents-operations.ts. Preview mode has no Vault store, so
      // these return empty results / synthetic values to keep render paths
      // from crashing.
      case 'vault_documents_list': {
        if (this.options.previewMode) {
          return { documents: [], nextCursor: null };
        }
        return this.proxyToBackend(msg);
      }
      case 'vault_documents_read': {
        if (this.options.previewMode) {
          return { content: '', contentType: 'text/plain' };
        }
        return this.proxyToBackend(msg);
      }
      case 'vault_documents_read_many': {
        if (this.options.previewMode) {
          return { results: [] };
        }
        return this.proxyToBackend(msg);
      }
      case 'vault_documents_download_url': {
        if (this.options.previewMode) {
          return { url: 'about:blank', expiresAt: Date.now() + 60_000 };
        }
        return this.proxyToBackend(msg);
      }
      case 'vault_documents_get_urls': {
        if (this.options.previewMode) {
          return { results: [] };
        }
        return this.proxyToBackend(msg);
      }
      case 'vault_documents_write': {
        if (this.options.previewMode) {
          return { documentId: `preview_vdoc_${Date.now()}` };
        }
        return this.proxyToBackend(msg);
      }
      case 'vault_documents_update':
      case 'vault_documents_delete': {
        if (this.options.previewMode) {
          return { success: true };
        }
        return this.proxyToBackend(msg);
      }
      case 'vault_documents_search': {
        if (this.options.previewMode) {
          return { matches: [] };
        }
        return this.proxyToBackend(msg);
      }
      case 'vault_documents_attach': {
        if (this.options.previewMode) {
          return {
            referenceId: `preview_ref_${Date.now()}`,
            attachedAt: new Date().toISOString(),
          };
        }
        return this.proxyToBackend(msg);
      }
      case 'vault_documents_detach': {
        if (this.options.previewMode) {
          return { success: true };
        }
        return this.proxyToBackend(msg);
      }
      case 'vault_documents_upload_init':
      case 'vault_documents_upload_finalize': {
        if (this.options.previewMode) {
          // The two-step upload protocol (init → PUT bytes to
          // presigned URL → finalize) needs a real PUT endpoint that
          // preview mode cannot provide. Returning a synthetic
          // uploadUrl like 'about:blank' would just produce a
          // confusing fetch failure when the SDK tries to upload.
          // Fail fast so plugin code can show a clearer message.
          throw new Error(
            `${msg.type} is not supported in preview mode (no presigned-URL endpoint). Test binary uploads in the published or dev-harness-live runtime.`,
          );
        }
        return this.proxyToBackend(msg);
      }

      case 'surface_invoke': {
        // Invocation Surface Pattern dispatch. Per-surface permission,
        // rate-limit, and validation gates all read from the surface
        // metadata server-side, so there's no entry in PERMISSION_MAP
        // here (matches the production backend's gating model). Preview
        // mode has no registered surfaces — return a typed error so
        // plugin code can render a placeholder rather than crash.
        if (this.options.previewMode) {
          throw new Error('surface_invoke is not supported in preview mode');
        }
        return this.proxyToBackend(msg);
      }

      // ── Arche Asset Library (contributor-curated published manifest) ──
      // The bridge backend resolves the manifest from the URL path's
      // archeId (server-trusted) and signs short-lived URLs against
      // the published entries. Preview mode has no manifest, so we
      // return an empty list / a synthetic URL.
      case 'arche_assets_list': {
        if (this.options.previewMode) {
          return { entries: [], nextCursor: null };
        }
        return this.proxyToBackend(msg);
      }
      case 'arche_assets_get_url': {
        if (this.options.previewMode) {
          return {
            assetId: 'preview',
            signedUrl: 'about:blank',
            expiresAt: Math.floor(Date.now() / 1000) + 60,
            contentType: 'image/png',
            width: 0,
            height: 0,
            sizeBytes: 0,
            name: null,
            altText: null,
            tags: [],
          };
        }
        return this.proxyToBackend(msg);
      }

      default:
        throw new Error(`Unknown bridge call type: ${msg.type}`);
    }
  }

  /**
   * Handle streaming entity invoke in preview mode via SSE.
   * Sends stream_token messages to the plugin as tokens arrive,
   * then returns the final result for the normal response message.
   */
  private async handlePreviewStreamingInvoke(
    messageId: string,
    payload: { entityId: string; input: string },
  ): Promise<unknown> {
    const token = await this.options.getAuthToken();
    const response = await fetch(
      `${this.options.bridgeApiUrl}/arches/plugin-builder/sessions/${this.options.archId.replace('preview_', '')}/ai-invoke`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ input: payload.input, entityId: payload.entityId, stream: true }),
      },
    );

    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'AI invocation failed' }));
      throw new Error(err.error?.message ?? 'AI invocation failed');
    }

    // If SSE streaming, consume the stream
    if (response.headers.get('content-type')?.includes('text/event-stream') && response.body) {
      let fullOutput = '';
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = line.slice(6);
          if (data === '[DONE]') continue;
          try {
            const parsed = JSON.parse(data);
            if (parsed.text) {
              fullOutput += parsed.text;
              // Forward token to plugin iframe
              this.options.iframe.contentWindow?.postMessage(
                { type: 'stream_token', messageId, text: parsed.text },
                '*',
              );
            }
            if (parsed.done && parsed.output) {
              fullOutput = parsed.output;
            }
          } catch {
            // Skip malformed SSE data
          }
        }
      }

      return { output: fullOutput };
    }

    // Non-streaming fallback
    return response.json();
  }

  /**
   * Handle streaming entity invoke for published plugins via the bridge endpoint.
   */
  private async handlePublishedStreamingInvoke(
    messageId: string,
    payload: { entityId: string; input: string },
  ): Promise<unknown> {
    const token = await this.options.getAuthToken();
    const response = await fetch(
      `${this.options.bridgeApiUrl}/plugins/bridge/${this.options.archId}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          type: 'entity_invoke',
          payload: { ...payload, stream: true },
        }),
      },
    );

    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'AI invocation failed' }));
      throw new Error(err.error?.message ?? 'AI invocation failed');
    }

    if (response.headers.get('content-type')?.includes('text/event-stream') && response.body) {
      let fullOutput = '';
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = line.slice(6);
          if (data === '[DONE]') continue;
          try {
            const parsed = JSON.parse(data);
            if (parsed.error) {
              throw new Error(parsed.error);
            }
            if (parsed.text) {
              fullOutput += parsed.text;
              this.options.iframe.contentWindow?.postMessage(
                { type: 'stream_token', messageId, text: parsed.text },
                '*',
              );
            }
            if (parsed.done && parsed.output) {
              fullOutput = parsed.output;
            }
          } catch (e) {
            if (e instanceof Error && e.message !== 'AI invocation failed') throw e;
          }
        }
      }

      return { output: fullOutput };
    }

    // Non-SSE fallback
    return response.json();
  }

  /**
   * Async-via-WebSocket dispatch for `image_generate`.
   *
   * Steps:
   *   1. POST the kickoff. The server returns `{ requestId, status:
   *      'pending' }` in ~10 ms.
   *   2. Subscribe to the shared `FiasWebSocket` for that requestId.
   *      `getWebSocket` lazily fetches its own Cognito-gated ticket
   *      via `authApi.getWebSocketTicket()`, so no extra auth wiring.
   *   3. Register `onImageComplete` + `onError` callbacks filtered by
   *      requestId. Broadcasts for any other in-flight request the
   *      user owns no-op.
   *   4. On match: resolve with the final `ImageInvokeResult` payload
   *      shape (strip broadcast-only `type` and `requestId` fields).
   *      The outer `handleMessage` in turn posts this into the iframe
   *      so the SDK's `await fias.images.generate(...)` resolves
   *      byte-for-byte the same as the pre-refactor synchronous path.
   *   5. 180 s safety timer: on fire, run a single `image-status`
   *      poll. If `completed` → resolve; `failed` → reject; still
   *      `pending` → reject with `image_generate_timeout`.
   *   6. Cleanup runs on EVERY terminal path (success / error /
   *      timeout / host destroy) — see `pendingImageRequests`
   *      comment + the cleanup() function below.
   *
   * If the server responds with anything other than `status: 'pending'`
   * (legacy code paths, future inline responses), this method falls
   * through to returning the body as-is.
   */
  private async handleAsyncImageGenerate(msg: BridgeMessage): Promise<unknown> {
    const initialResponse = (await this.proxyToBackend(msg)) as {
      requestId?: string;
      status?: string;
      [key: string]: unknown;
    };
    if (initialResponse?.status !== 'pending' || typeof initialResponse.requestId !== 'string') {
      return initialResponse;
    }
    const requestId = initialResponse.requestId;
    const ws = getWebSocket(WEBSOCKET_ENDPOINT);

    // Connect lazily and subscribe. The WS service authenticates on
    // `?ticket=<single-use>` — Cognito JWTs won't validate as tickets,
    // so use `authApi.getWebSocketTicket()` (the same provider
    // `useStreaming` already uses). Connection state is process-wide
    // on the FiasWebSocket singleton, so if the broader app already
    // connected the socket this call resolves immediately; otherwise
    // it brings the socket up the first time a plugin asks for it.
    if (ws.getState() !== 'connected') {
      await ws.connect(() => authApi.getWebSocketTicket());
    }
    ws.subscribe(requestId);

    return new Promise<unknown>((resolve, reject) => {
      let settled = false;
      const finish = (cb: () => void): void => {
        if (settled) return;
        settled = true;
        const entry = this.pendingImageRequests.get(requestId);
        if (entry) {
          entry.cleanup();
          this.pendingImageRequests.delete(requestId);
        }
        cb();
      };

      const unsubComplete = ws.onImageComplete((data) => {
        if (data.requestId !== requestId) return;
        // Reconstruct the pre-refactor `ImageInvokeResult` shape
        // byte-for-byte. The broadcast carries broadcast-only metadata
        // (`type`, `requestId`) that the SDK contract did not include
        // — stripping them keeps the iframe-bound payload identical to
        // what the synchronous HTTP response returned pre-refactor.
        const payload: Record<string, unknown> = {
          imageUrl: data.imageUrl,
          imageRef: data.imageRef,
          fileId: data.fileId,
          revisedPrompt: data.revisedPrompt,
          provider: data.provider,
          model: data.model,
          costCents: data.costCents,
        };
        if (data.deprecation) payload.deprecation = data.deprecation;
        finish(() => resolve(payload));
      });

      const unsubError = ws.onError((code, message, errRequestId) => {
        // Only fail THIS entry. Generic connection errors (no requestId
        // — e.g. CONNECTION_FAILED after max reconnect attempts) reach
        // every listener; per-request errors broadcast by the server
        // (e.g. image_generate failures) carry the originating
        // requestId via the extended `ErrorCallback` signature. Both
        // cases land here; the filter below routes them correctly:
        //   - requestId === ours → reject this pending image entry
        //   - requestId undefined → treat as a connection-level error
        //     and reject every pending entry (callers can retry)
        //   - requestId for a different request → ignore
        if (errRequestId !== undefined && errRequestId !== requestId) return;
        finish(() => reject(new Error(`${code}: ${message}`)));
      });

      const onTimeout = (): void => {
        // Fall back to the poll endpoint before giving up.
        this.pollImageStatus(requestId)
          .then((status) => {
            if (status.kind === 'completed') {
              finish(() => resolve(status.payload));
            } else if (status.kind === 'failed') {
              finish(() => reject(new Error(`${status.code}: ${status.message}`)));
            } else {
              finish(() => reject(new Error('image_generate_timeout')));
            }
          })
          .catch((pollErr: unknown) => {
            const msg = pollErr instanceof Error ? pollErr.message : 'poll_failed';
            finish(() => reject(new Error(`image_generate_timeout: ${msg}`)));
          });
      };
      const timer = setTimeout(onTimeout, 180_000);

      const cleanup = (): void => {
        unsubComplete();
        unsubError();
        clearTimeout(timer);
        // Unsubscribe THIS request only. Parameterless `unsubscribe()`
        // would clear every subscription on the shared singleton —
        // including a sibling internal-arche generation in another tab
        // and any other pending plugin image_generate. The targeted
        // form leaves those untouched.
        ws.unsubscribe(requestId);
      };

      this.pendingImageRequests.set(requestId, { resolve, reject, cleanup, timer });
    });
  }

  /**
   * Polls `GET /v1/plugins/image-status/:requestId` once. Returns a
   * discriminated union mirroring the server-side 4-state contract.
   * Called only from the 180 s safety-timer path; the happy path
   * resolves via WebSocket.
   */
  private async pollImageStatus(
    requestId: string,
  ): Promise<
    | { kind: 'pending' }
    | { kind: 'completed'; payload: unknown }
    | { kind: 'failed'; code: string; message: string }
    | { kind: 'not_found' }
  > {
    try {
      const token = await this.options.getAuthToken();
      const resp = await fetch(
        `${this.options.bridgeApiUrl}/plugins/image-status/${encodeURIComponent(requestId)}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (resp.status === 404) return { kind: 'not_found' };
      const body = (await resp.json()) as {
        status?: string;
        payload?: unknown;
        error?: { code?: string; message?: string };
      };
      if (body.status === 'completed') return { kind: 'completed', payload: body.payload };
      if (body.status === 'failed') {
        return {
          kind: 'failed',
          code: body.error?.code ?? 'GENERATION_FAILED',
          message: body.error?.message ?? 'Image generation failed',
        };
      }
      return { kind: 'pending' };
    } catch {
      return { kind: 'pending' };
    }
  }

  private async proxyToBackend(msg: BridgeMessage): Promise<unknown> {
    const token = await this.options.getAuthToken();
    const response = await fetch(
      `${this.options.bridgeApiUrl}/plugins/bridge/${this.options.archId}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          type: msg.type,
          payload: msg.payload,
        }),
      },
    );

    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Bridge call failed' }));
      throw new Error(err.error?.message ?? 'Bridge call failed');
    }

    return response.json();
  }

  private checkRateLimit(type: string, payload: unknown): void {
    // Burst detection first (catches runaway loops in <5s)
    this.checkBurst(type, payload);

    const limit = RATE_LIMITS[type];
    if (!limit) return;

    const now = Date.now();
    const bucket = this.rateLimits.get(type);

    if (!bucket || now - bucket.windowStart > 60_000) {
      this.rateLimits.set(type, { count: 1, windowStart: now });
      return;
    }

    if (bucket.count >= limit.maxPerMinute) {
      const target = describeBridgeTarget(type, payload);
      const targetSuffix = target ? ` (${target})` : '';
      throw new Error(
        `Rate limit exceeded for ${type}${targetSuffix}: max ${limit.maxPerMinute}/minute`,
      );
    }

    bucket.count++;
  }

  private checkBurst(type: string, payload: unknown): void {
    const now = Date.now();
    const bucket = this.burstBuckets.get(type);
    const target = describeBridgeTarget(type, payload);
    const targetSuffix = target ? ` (${target})` : '';

    // If currently in cooldown, block silently (onLog already fired on first breach)
    if (bucket?.blockedUntil && now < bucket.blockedUntil) {
      throw new BridgeBurstError(
        `Bridge call "${type}"${targetSuffix} temporarily blocked — rapid-fire loop detected. ` +
          `Check useEffect dependency arrays, or debounce writes if this is a usePersistentState key being updated at high frequency.`,
      );
    }

    // Reset expired burst windows
    if (!bucket || now - bucket.windowStart > BURST_WINDOW_MS) {
      this.burstBuckets.set(type, { count: 1, windowStart: now });
      return;
    }

    bucket.count++;

    if (bucket.count > BURST_THRESHOLD) {
      bucket.blockedUntil = now + BURST_COOLDOWN_MS;
      const msg =
        `Runaway loop detected: "${type}"${targetSuffix} called ${bucket.count} times in ${BURST_WINDOW_MS / 1000}s. ` +
        `Calls blocked for ${BURST_COOLDOWN_MS / 1000}s. ` +
        `Check useEffect dependency arrays, or debounce writes if this is a usePersistentState key being updated at high frequency.`;
      this.options.onLog?.('error', msg);
      throw new BridgeBurstError(
        `Bridge call "${type}"${targetSuffix} temporarily blocked — rapid-fire loop detected.`,
      );
    }
  }

  private sendToPlugin(message: BridgeMessage): void {
    // targetOrigin '*' is required because sandboxed iframes without allow-same-origin
    // have an opaque (null) origin. Security is enforced by checking event.source
    // against iframe.contentWindow on incoming messages (line 113).
    this.options.iframe.contentWindow?.postMessage(message, '*');
  }

  private sendResponse(messageId: string, payload: unknown): void {
    this.sendToPlugin({
      type: 'response' as string,
      messageId,
      payload,
    });
  }

  private sendError(messageId: string, error: string): void {
    // See sendToPlugin() for why '*' is used as targetOrigin.
    this.options.iframe.contentWindow?.postMessage(
      {
        type: 'response',
        messageId,
        payload: null,
        error,
      },
      '*',
    );
  }
}
